import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Package, Truck } from 'lucide-react';
import { ordersAPI } from '../api/orders';
import ContinueOrderPayment from '../components/checkout/ContinueOrderPayment';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useCurrencyStore } from '../store/currencyStore';
import toast from 'react-hot-toast';

const carrierTrackingUrl = (carrier: string | undefined, trackingNumber: string): string | null => {
  const c = (carrier || '').toUpperCase();
  if (c === 'USPS') return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
  if (c === 'UPS') return `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber}`;
  if (c === 'FEDEX') return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
  if (c === 'DHL') return `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${trackingNumber}`;
  return null;
};

const formatDate = (date: string | null | undefined) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [shippingAddress, setShippingAddress] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { format } = useCurrencyStore();

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const res = await ordersAPI.getOrder(id);
        if (res.success && res.data) {
          setOrder(res.data.order);
          setItems(res.data.items || []);
          setShippingAddress((res.data as any).shipping_address || null);
        }
      } catch {
        toast.error('Failed to fetch order');
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const statusColors: Record<string, string> = {
    pending: 'text-amber-600 bg-amber-50',
    processing: 'text-blue-600 bg-blue-50',
    shipped: 'text-purple-600 bg-purple-50',
    delivered: 'text-green-600 bg-green-50',
    cancelled: 'text-red-500 bg-red-50',
  };

  const statusSteps = ['pending', 'processing', 'shipped', 'delivered'];
  const statusDates: Record<string, string | null> = {
    pending: formatDate(order?.created_at),
    processing: null,
    shipped: formatDate(order?.shipped_at),
    delivered: null,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xs tracking-widest uppercase text-nude mb-4">
            Order not found
          </p>
          <Link to="/account" className="btn-primary">
            Back to Account
          </Link>
        </div>
      </div>
    );
  }

  const currentStep = statusSteps.indexOf(order.status);
  const trackingUrl = order.tracking_number
    ? carrierTrackingUrl(order.carrier, order.tracking_number)
    : null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 animate-fade-in">
      {/* Back */}
      <Link
        to="/account"
        className="flex items-center space-x-2 text-xs tracking-widest uppercase text-nude hover:text-primary transition-colors mb-8"
      >
        <ChevronLeft size={14} />
        <span>Back to Account</span>
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-12">
        <div>
          <p className="text-xs tracking-widest uppercase text-nude mb-2">
            Order Details
          </p>
          <h1 className="font-serif text-2xl sm:text-4xl text-primary break-words">
            {order.order_number}
          </h1>
          <p className="text-xs text-nude mt-2">
            {formatDate(order.created_at)}
          </p>
        </div>
        <span className={`text-xs tracking-widest uppercase px-4 py-2 rounded ${
          statusColors[order.status] || 'text-primary bg-light'
        }`}>
          {order.status}
        </span>
      </div>

      {/* Order Progress */}
      {order.status !== 'cancelled' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between relative">
            <div className="absolute left-0 right-0 top-3 sm:top-4 h-px bg-light z-0" />
            <div
              className="absolute left-0 top-3 sm:top-4 h-px bg-primary z-0 transition-all duration-500"
              style={{
                width: currentStep >= 0
                  ? `${(currentStep / (statusSteps.length - 1)) * 100}%`
                  : '0%',
              }}
            />
            {statusSteps.map((step, index) => (
              <div
                key={step}
                className="flex flex-col items-center z-10 space-y-1.5 sm:space-y-2 flex-1 px-0.5"
              >
                <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                  index <= currentStep
                    ? 'bg-primary border-primary'
                    : 'bg-white border-light'
                }`}>
                  {index < currentStep ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                      stroke="white" strokeWidth="3" strokeLinecap="round"
                      strokeLinejoin="round" className="sm:w-3 sm:h-3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                      index === currentStep ? 'bg-white' : 'bg-light'
                    }`} />
                  )}
                </div>
                <span className={`text-[9px] sm:text-xs tracking-normal sm:tracking-widest uppercase text-center leading-tight break-words ${
                  index <= currentStep ? 'text-primary' : 'text-nude'
                }`}>
                  {step}
                </span>
                {statusDates[step] && (
                  <span className="text-[8px] sm:text-[10px] text-nude text-center leading-tight">
                    {statusDates[step]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Tracking Info */}
      {order.tracking_number && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-light p-6 mb-12 flex items-start space-x-4"
        >
          <Truck size={20} className="text-primary mt-1 flex-shrink-0" />
          <div>
            <h3 className="font-serif text-lg text-primary mb-1">
              Your order is on its way
            </h3>
            <p className="text-xs text-secondary">
              <span className="text-nude">Carrier:</span> {order.carrier || 'USPS'}
            </p>
            <p className="text-xs text-secondary mt-1">
              <span className="text-nude">Tracking Number:</span>{' '}
              {trackingUrl ? (
                <a
                  href={trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium tracking-wide text-primary underline hover:text-nude transition-colors"
                >
                  {order.tracking_number}
                </a>
              ) : (
                <span className="font-medium tracking-wide">{order.tracking_number}</span>
              )}
            </p>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Order Items */}
        <div className="lg:col-span-2 space-y-6">
          <h2 className="font-serif text-xl text-primary">Items</h2>
          {items.map((item) => (
            <div key={item.id} className="flex space-x-4 border-b border-light pb-6">
              <div className="w-16 h-20 bg-light flex-shrink-0">
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.product_name || 'Product'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package size={24} className="text-nude m-auto mt-6" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium tracking-wide">
                  {item.product_name || `Product #${item.product_id?.slice(0, 8)}`}
                </p>
                <p className="text-xs text-nude mt-1">
                  {item.size && `Size: ${item.size}`}
                  {item.color && ` / ${item.color}`}
                </p>
                <p className="text-xs text-nude">Qty: {item.quantity}</p>
              </div>
              <p className="text-sm">
                {format(Number(item.price) * item.quantity)}
              </p>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="space-y-6">
          <div className="bg-light p-6">
            <h2 className="font-serif text-lg text-primary mb-4">Summary</h2>
            <div className="space-y-3 text-xs">
              <div className="flex justify-between">
                <span className="text-nude">Subtotal</span>
                <span>{format(Number(order.subtotal))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-nude">Shipping</span>
                <span>{format(Number(order.shipping_cost))}</span>
              </div>
              {Number((order as any).tax) > 0 && (
                <div className="flex justify-between">
                  <span className="text-nude">Tax</span>
                  <span>{format(Number((order as any).tax))}</span>
                </div>
              )}
              {Number(order.discount_amount) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-{format(Number(order.discount_amount))}</span>
                </div>
              )}
              <div className="flex justify-between font-medium pt-3 border-t border-nude/30 text-sm">
                <span className="tracking-widest uppercase">Total</span>
                <span>{format(Number(order.total))}</span>
              </div>
            </div>
          </div>

          {shippingAddress && (
            <div className="bg-light p-6">
              <h2 className="font-serif text-lg text-primary mb-4">Ship To</h2>
              <div className="text-xs text-secondary leading-relaxed">
                <p className="font-medium tracking-wide text-primary mb-1">
                  {shippingAddress.first_name} {shippingAddress.last_name}
                </p>
                <p>
                  {shippingAddress.address_line1}
                  {shippingAddress.address_line2 && `, ${shippingAddress.address_line2}`}
                </p>
                <p>
                  {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}
                </p>
                <p>{shippingAddress.country}</p>
              </div>
            </div>
          )}

          {(order as any).shipping_type && (order as any).shipping_type !== 'standard' && (
            <div className="bg-amber-50 border border-amber-200 p-6">
              <h2 className="font-serif text-lg text-primary mb-2">Shipping</h2>
              <p className="text-xs tracking-widest uppercase text-amber-700 mb-2">
                {(order as any).shipping_type === 'pickup'
                  ? 'Pick Up In Store (Nigeria)'
                  : (order as any).shipping_type === 'nigeria_local'
                  ? 'Local Delivery (Nigeria)'
                  : 'International Delivery'}
              </p>
              {(order as any).shipping_type !== 'pickup' && (
                <p className="text-xs text-secondary leading-relaxed">
                  Shipping cost isn't charged yet — our team will confirm the final delivery cost with you directly on WhatsApp.
                </p>
              )}
            </div>
          )}

          <div className="bg-light p-6">
            <h2 className="font-serif text-lg text-primary mb-4">Payment</h2>
            <div className="text-xs space-y-2">
              <div className="flex justify-between">
                <span className="text-nude">Method</span>
                <span className="capitalize">
                  {order.payment_method?.replace('_', ' ') || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-nude">Status</span>
                <span className={
                  order.payment_status === 'paid'
                    ? 'text-green-600'
                    : 'text-amber-600'
                }>
                  {order.payment_status}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-light p-6">
            <h2 className="font-serif text-lg text-primary mb-4">Invoice</h2>
            {order.payment_status === 'paid' ? (
              <button
                onClick={() => ordersAPI.downloadInvoice(order.id, order.order_number)}
                className="btn-secondary w-full text-xs"
              >
                Download Invoice (PDF)
              </button>
            ) : (
              <p className="text-xs text-nude leading-relaxed">
                Your invoice will be available here once payment is confirmed.
              </p>
            )}
          </div>

          {order.status === 'pending' && order.payment_status === 'unpaid' && (
            <ContinueOrderPayment orderId={order.id} orderTotalUsd={Number(order.total)} />
          )}

          {order.status === 'pending' && (
            <button
              onClick={async () => {
                try {
                  await ordersAPI.cancelOrder(order.id);
                  toast.success('Order cancelled');
                  setOrder({ ...order, status: 'cancelled' });
                } catch {
                  toast.error('Failed to cancel order');
                }
              }}
              className="btn-secondary w-full text-xs"
            >
              Cancel Order
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;